import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Tv, Play } from 'lucide-react';
import InteractiveArticleViewer from '../components/InteractiveArticleViewer';
import { ArticleComments, ShareWidget } from '../components/ArticleInteractions';
import AdSenseAd from '../components/AdSenseAd';

/**
 * SWAC TV / How to Watch SWAC Football This Weekend
 * 
 * Comprehensive gameday guide featuring streaming platforms, live stats,
 * and all the information fans need to watch SWAC football.
 */
const SWACTVArticlePage = () => {
  const articleUrl = '/articles/swac-tv-weekend-guide.html';
  
  // Article metadata for social cards and SEO
  const articleMeta = {
    title: "How to Watch SWAC Football This Weekend",
    subtitle: "SWAC TV, Prime Video, ESPN+ & Live Stats — Your Complete Gameday Guide",
    description: "Complete guide to watching SWAC football games this weekend. Find SWAC TV on Prime Video, ESPN+, live stats links, and streaming platforms for Jackson State, Alabama A&M, Grambling, Alcorn State, and all SWAC teams.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/12mdvn7w_9ABE93F3-1197-42CC-98EE-6245B2328755.png",
    url: "https://shelovesthee.com/swac-tv",
    author: "SheLovesThee Sports",
    category: "SWAC Football"
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* SEO & Social Cards Meta Tags */}
      <Helmet>
        <title>{`${articleMeta.title} | SheLovesThee`}</title>
        <meta name="description" content={articleMeta.description} />
        <meta name="author" content={articleMeta.author} />
        <meta name="keywords" content="how to watch SWAC football this weekend, how to watch SWAC TV, where to watch SWAC TV, SWAC TV Prime Video, SWAC football games today, SWAC football live stream, SWAC games this weekend, SWAC football schedule, SWAC live stats, Jackson State vs Alabama A&M, Alcorn State vs Grambling, Alabama State vs Bethune-Cookman, UAPB vs Southern, Texas Southern vs FAU" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={articleMeta.title} />
        <meta property="og:description" content={articleMeta.description} />
        <meta property="og:image" content={articleMeta.image} />
        <meta property="og:url" content={articleMeta.url} />
        <meta property="og:site_name" content="SheLovesThee" />
        <meta property="article:author" content={articleMeta.author} />
        <meta property="article:section" content={articleMeta.category} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={articleMeta.title} />
        <meta name="twitter:description" content={articleMeta.description} />
        <meta name="twitter:image" content={articleMeta.image} />
        <meta name="twitter:site" content="@SheLovesThee" />
      </Helmet>

      {/* NOW STREAMING Banner */}
      <div className="bg-gradient-to-r from-[#ff9900] via-[#146eb4] to-[#ff9900] text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <Tv className="w-5 h-5 animate-pulse" />
          <span className="font-black text-sm sm:text-base uppercase tracking-wide">
            NOW STREAMING: SWAC Football on Prime Video
          </span>
          <Play className="w-5 h-5 fill-white" />
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="bg-[#00204E] text-white py-3 px-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white hover:text-yellow-400 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Home</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/swac-scoreboard" 
              className="text-white hover:text-yellow-400 transition-colors text-sm hidden sm:inline"
            >
              Scores
            </Link>
            <Link 
              to="/swac-pickem" 
              className="text-white hover:text-yellow-400 transition-colors text-sm"
            >
              Pick'em
            </Link>
            <Link 
              to="/gameday" 
              className="text-white hover:text-yellow-400 transition-colors text-sm"
            >
              Gameday
            </Link>
          </div>
        </div>
      </div>

      {/* Article Content - Using InteractiveArticleViewer */}
      <main className="pb-8">
        <InteractiveArticleViewer 
          articleUrl={articleUrl}
          title={articleMeta.title}
        />
        
        {/* AdSense Ad - After article content, before share */}
        <div className="max-w-4xl mx-auto px-4 mt-6" data-testid="article-ad-placement">
          <AdSenseAd 
            adSlot="2800195719"
            label="Advertisement"
          />
        </div>
        
        {/* Share & Comments Section */}
        <div className="max-w-4xl mx-auto px-4 mt-8">
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h3 className="text-lg font-bold text-[#00204E] mb-4">SHARE THIS ARTICLE</h3>
            <ShareWidget 
              articleId="swac-tv-how-to-watch"
              articleTitle={articleMeta.title}
              articleUrl={articleMeta.url}
            />
          </div>
        </div>

        {/* Comments Section */}
        <div className="max-w-4xl mx-auto px-4 mt-6">
          <ArticleComments 
            articleId="swac-tv-how-to-watch"
            title="Discussion"
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#00204E] text-white py-6 text-center">
        <p className="text-gray-400 text-sm">
          SheLovesThee Sports Media • HBCU Sports. Real Talk. Thee I Love.
        </p>
        <p className="text-gray-500 text-xs mt-2">
          SWAC TV streaming information accurate as of publication. Check official sources for updates.
        </p>
      </footer>
    </div>
  );
};

export default SWACTVArticlePage;
